import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Row,
  Container,
  Col,
  Card,
  CardBody,
  CardTitle,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useRole } from 'hooks/useRole';
import { useAuth } from 'hooks/useAuth';
import moment from 'moment';
import SplineArea from './SplineArea';
import {
  getMemberStatusCountsApi,
  getClaimStatusCountsApi,
  getPaymentsStatusCountsApi,
} from 'api/report';

//Import Breadcrumb
import Breadcrumbs from '../../components/Common/Breadcrumb';

//i18n
import { withTranslation } from 'react-i18next';
import { getSearchColumns, getUserAddress } from 'utils/tools';

const Dashboard = (props) => {
  const { isAuthenticated } = useAuth();
  const {
    isFacilitator,
    isSiteCord,
    isCountyCoordinator,
    isRegionalCountyCoordinator,
    isClusterLeader,
    isData,
    addresses,
  } = useRole();
  //meta title
  document.title = 'Dashboard | SHOFCO - SUN Portal';
  const dateFormat = 'DD-MM-YYYY';
  const queryClient = useQueryClient();
  const { wardId, constituencyId, countyId, regionId } = (isAuthenticated && addresses)
    ? addresses[0]
    : {};
  const [pagination, setPagination] = useState({
    pageSize: 10,
    pageNumber: 1,
    sortBy: 'id',
    ascendingSort: false,
    wardIds: getSearchColumns()?.find((t) => t?.name === 'wardIds')?.value,
    searchColumns: [
      ...(isFacilitator
        ? [
          {
            name: 'wardId',
            value: wardId,
          },
        ]
        : []),
      ...(isCountyCoordinator
        ? [
          {
            name: 'countyId',
            value: countyId,
          },
          {
            name: 'countyIds',
            values: addresses?.map((c) => c.countyId),
          },
        ]
        : []),
      ...(isRegionalCountyCoordinator
        ? [
          {
            name: 'regionIds',
            values: addresses?.map((c) => c.regionId),
          },
        ]
        : []),
      ...(isClusterLeader
        ? [
          {
            name: 'regionId',
            value: regionId,
          },
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      ...(isSiteCord
        ? [
          {
            name: 'constituencyId',
            value: constituencyId,
          },
          {
            name: 'constituencyIds',
            values: addresses?.map((c) => c.constituencyId),
          },
        ]
        : []),
      {
        name: 'endDate',
        value: moment().format(dateFormat),
      },
      {
        name: 'startDate',
        value: moment().startOf('year').format(dateFormat),
      },
    ],
  });

  const startDate = moment().startOf('year').format(dateFormat);
  const endDate = moment().format(dateFormat);

  const { mutate: mutateMemberCounts, data: memberSummary } = useMutation(
    (payload) => getMemberStatusCountsApi(payload),
    {
      onSuccess: () => {
        // dispatch(setMemberSummary(res?.data));
      },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  const { mutate: mutateClaimCounts, data: claimSummary } = useMutation(
    (payload) => getClaimStatusCountsApi(payload),
    {
      onSuccess: () => { },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  const { mutate: mutatePaymentCounts, data: paymentsSummary } = useMutation(
    (payload) => getPaymentsStatusCountsApi(payload),
    {
      onSuccess: () => { },
      onSettled: () => {
        queryClient.invalidateQueries('get-all-counties');
      },
    },
  );

  useEffect(() => {
    const address = getUserAddress();
    let wardIds = pagination.wardIds;
    let countyIds = pagination.searchColumns.find(s => s.name === 'countyIds')?.values;
    let regionIds = pagination.searchColumns.find(s => s.name === 'regionIds')?.values;
    let constituencyIds = pagination.searchColumns.find(s => s.name === 'constituencyIds')?.values;
    mutatePaymentCounts({
      fromDate: startDate,
      toDate: endDate,
      wardIds,
      countyIds,
      regionIds,
      constituencyIds,
      address: address == null ? {} : address,
    });

    mutateClaimCounts({
      fromDate: startDate,
      toDate: endDate,
      wardIds,
      countyIds,
      regionIds,
      constituencyIds,
      address: address == null ? {} : address,
    });
    mutateMemberCounts({
      fromDate: startDate,
      toDate: endDate,
      wardIds,
      countyIds,
      regionIds,
      constituencyIds,
      address: address == null ? {} : address,
    });
  }, []);

  const quickActions = [
    { to: '/members/create', label: 'Add Member', icon: 'mdi-account-plus-outline' },
    { to: '/payments/create', label: 'Pay Premium', icon: 'mdi-cash-multiple' },
    { to: '/claims/create', label: 'Make Claim', icon: 'mdi-file-document-edit-outline' },
  ];

  const reportPeriod = `${moment().startOf('year').format('DD MMM YYYY')} – ${moment().format(
    'DD MMM YYYY',
  )}`;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          header={props.t('Admin Dashboard')}
          title={props.t('Home')}
          breadcrumbItem={props.t('Dashboards')}
        />
        <Row className="align-items-center">
          {!isData && (
            <Col xs={12}>
              <div className="d-flex flex-wrap justify-content-md-end gap-2 mb-3">
                {quickActions.map((action) => (
                  <Link key={action.to} to={action.to} className="btn btn-primary btn-sm">
                    <i className={`mdi ${action.icon} align-middle me-1`} />
                    {action.label}
                  </Link>
                ))}
              </div>
            </Col>
          )}
        </Row>

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody>
                <div className="d-flex align-items-start justify-content-between mb-3">
                  <div>
                    <CardTitle className="mb-1">Custom Snapshot Reports</CardTitle>
                    <p className="text-muted mb-0 font-size-12">{reportPeriod}</p>
                  </div>
                </div>
                <SplineArea
                  dataColors='["--bs-success", "--bs-warning", "--bs-primary"]'
                  claims={claimSummary?.data || []}
                  premiums={paymentsSummary?.data || []}
                  members={memberSummary?.data || []}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

Dashboard.propTypes = {
  t: PropTypes.any,
  chartsData: PropTypes.any,
  onGetChartsData: PropTypes.func,
};

export default withTranslation()(Dashboard);
